import { cloneDeep, get } from 'lodash';
import { z } from 'zod';

import QuestionsService from 'services/QuestionsService';

import { Answers } from 'types/zod/answers';
import { Question } from 'types/zod/question';
import { TrustFundingAsset } from 'types/zod/trust-funding-asset';

const service = new QuestionsService();

const TrustFundingService = {
    getCategories: (questionnaire: Question): string[] => {
        const sourceQuestion = get(questionnaire, 'assets._loop.0.asset_category');
        const question = z.record(z.string(), z.any()).parse(sourceQuestion || {});
        const keys = Object.keys(question || {});
        return keys.filter((key) => !key.startsWith('_'));
    },

    getAssets: (answers: Answers): TrustFundingAsset[] => {
        return (answers?.assets?._loop || []).map((asset: any, index: number) => {
            const category = Object.keys(asset.asset_category)[0];
            return {
                id: index,
                done: !!asset.asset_category[category]?.asset?.asset_checkbox?.checked,
                name: asset.asset_category[category]?.asset?.asset_name,
                category: category,
            };
        });
    },

    toggleDone: (answers: Answers, assetId: TrustFundingAsset['id']): Answers => {
        const toggledAnswers = cloneDeep(answers);
        const asset = toggledAnswers.assets._loop[assetId];
        const category = Object.keys(asset.asset_category)[0];
        asset.asset_category[category].asset.asset_checkbox = {
            checked: !asset.asset_category[category].asset.asset_checkbox?.checked,
        };
        return toggledAnswers;
    },

    deleteAsset: (answers: Answers, assetId: TrustFundingAsset['id']): Answers => {
        const answersWithoutAsset = cloneDeep(answers);
        answersWithoutAsset.assets._loop.splice(assetId, 1);
        return answersWithoutAsset;
    },

    getAssetQuestionPath: (assetId: TrustFundingAsset['id'] | 'new'): string => {
        return `assets._loop.${assetId}.asset_category`;
    },

    removeUnfinishedAssets: (answers: Answers, questionnaire: Question): Answers => {
        const q = service.enhanceQuestionWithPathsAndNames(questionnaire);
        const assetAnswers = answers.assets?._loop || [];
        if (!assetAnswers.length) {
            return answers;
        }
        const finishedAssetAnswers = [];
        for (const asset of assetAnswers) {
            const { isFinished } = service.getQuestionnaireProgress(
                q,
                q,
                {
                    assets: {
                        _loop: [asset],
                    },
                },
                {},
            );
            if (isFinished) {
                finishedAssetAnswers.push(asset);
            }
        }
        const newAnswers = cloneDeep(answers);
        newAnswers.assets._loop = finishedAssetAnswers;
        return newAnswers;
    },
};

export default TrustFundingService;
